import { useQuery } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import React from 'react';
import {
  CopyableText,
  ErrorComponents,
  FormattedAddress,
  OpenSidebarButton,
  SummaryGridItem,
  SummarySection,
  isAccess,
  isResourceAccess,
} from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { TaxComponent } from '@css-xp/base/tax-exempt/src/tax-component';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import {
  AuthenticatedUserProfile,
  Maybe,
  Permissions,
  Resources,
  UserRedirectionType,
} from '@atlassian/cssxp-typescommon';
import { fetchTXAPartnerAccount } from '../queries/fetch-partner-account';
import { EditBillingDetails } from '../sidebar-panels/edit-billing-details-sidebar';
import { SetPaymentMethod } from '../sidebar-panels/set-card-payment-default-sidebar';
import { SIDEBAR } from '../transaction-account-page';
import { Link } from 'react-router-dom';

interface SummaryProps {
  transactionAccountId: string | undefined;
  transactionAccountNumber: string | undefined | null;
  billToPartyPricingEligibilty: string | undefined | null;
  billToAddress: CommerceAddress | undefined | null;
  editBillingDetailsFormProps: EditBillingDetails;
  taxExemptFlag: string;
  invoiceGroupId?: string;
  partnerAccountId?: Maybe<string> | undefined;
  setIsAddressEditable?: React.Dispatch<React.SetStateAction<boolean>>;
}

const { BILL_TO_PARTY_WRITE } = Permissions;

const Summary = ({
  transactionAccountId,
  editBillingDetailsFormProps,
  taxExemptFlag,
  billToPartyPricingEligibilty,
  billToAddress,
  transactionAccountNumber,
  invoiceGroupId,
  partnerAccountId,
  setIsAddressEditable,
}: SummaryProps) => {
  // const taxExemptFlag = billToAddress?.taxExempt ? 'Yes' : 'No';
  // const billToPartyPricingEligibilty = transactionAccount?.billToPartyPricingEligibilty;

  const partnerAccountPageResult = useQuery<
    generatedTypesNew.FetchTxaPartnerAccountQuery,
    generatedTypesNew.FetchTxaPartnerAccountQueryVariables
  >(fetchTXAPartnerAccount, {
    variables: { id: partnerAccountId ?? '' },
    fetchPolicy: 'no-cache',
    skip: !partnerAccountId,
  });

  const { redirectionData = [] } = useApplicationContext() as AuthenticatedUserProfile;
  const salesforceObj = redirectionData.find((item) => item.name === UserRedirectionType.LightningSalesforce);
  const salesforceUrl = salesforceObj?.url;
  React.useEffect(() => {
    if (partnerAccountPageResult.data && setIsAddressEditable) {
      setIsAddressEditable(false);
    }
  }, [partnerAccountPageResult]);

  const { data, loading, error } = partnerAccountPageResult;
  if (loading) return <LoadingIndicator />;
  if (error && !data) return <ErrorComponents.GenericErrorPage error={error} />;

  const txaPartnerAccount = data?.txaPartnerAccount;
  const partnerType = txaPartnerAccount?.type || '';
  const SFDC_id = txaPartnerAccount?.sourceId || '';
  const partnerLevel = txaPartnerAccount?.programLevelGlobal || '';
  const payOnAccount = txaPartnerAccount?.payOnAccount ? 'Yes' : 'No';
  const specilizations = txaPartnerAccount?.specializations || '';
  const verifiedExpertise = txaPartnerAccount?.verifiedExpertise;

  return (
    <SummarySection columns="2fr 1fr" className="transaction-account-summary transaction-summary-section">
      <SummaryGridItem className="summary-grid-item-internal">
        <SummarySection className="summary-section-internal" columns="1fr 1fr 1fr">
          <SummaryGridItem className="section-item-internal">
            <div>
              {/* className="bill-to-address" */}
              <label>
                {billToAddress && billToAddress.addressLine1 ? 'Billing address' : 'No address information'}
              </label>
              <div>
                {/* className="address-details" */}
                <FormattedAddress
                  addressLine1={billToAddress?.addressLine1}
                  addressLine2={billToAddress?.addressLine2}
                  city={billToAddress?.city}
                  country={billToAddress?.country}
                  postcode={billToAddress?.postcode}
                  state={billToAddress?.state}
                />
              </div>
              {isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(BILL_TO_PARTY_WRITE) && (
                <OpenSidebarButton
                  sidebar={SIDEBAR.EDIT_TRANSACTION_ACCOUNT}
                  payload={{
                    ...editBillingDetailsFormProps,
                  }}
                  appearance="link"
                  sidebarOptions={{
                    sidebarWidth: '600px',
                    isOverlay: true,
                  }}
                  testId="edit-transaction-account-action"
                  spacing="none"
                  label="Edit"
                >
                  Edit
                </OpenSidebarButton>
              )}
            </div>
          </SummaryGridItem>

          <TaxComponent
            billToAddress={billToAddress}
            taxExemptFlag={taxExemptFlag}
            transactionAccountNumber={transactionAccountNumber}
          />
        </SummarySection>
        <SummarySection className="summary-section-internal" columns="1fr 1fr 1fr">
          <SummaryGridItem className="section-item-internal">
            <>
              <label>Billing account ID</label>
              <div>{(transactionAccountNumber && <CopyableText text={transactionAccountNumber} />) ?? ''}</div>
            </>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <>
              <label>Billing UUID</label>
              <div>{(transactionAccountId && <CopyableText text={transactionAccountId} />) ?? ''}</div>
            </>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <label>Pricing eligibility</label>
            <div data-testid="plan">{capitalize(billToPartyPricingEligibilty ?? 'Commercial')}</div>
          </SummaryGridItem>
        </SummarySection>

        {txaPartnerAccount && (
          <div data-testid="partner-info">
            <SummarySection className="summary-section-internal" columns="1fr 1fr 1fr">
              <SummaryGridItem className="section-item-internal">
                <>
                  <label>Partner Type</label>
                  <div data-testid="partner-type">{partnerType}</div>
                </>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal">
                <>
                  <label>Partner Level</label>
                  <div data-testid="partner-level">{partnerLevel}</div>
                </>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal">
                <label>SFDC ID</label>
                <div data-testid="sfdc-id">{(SFDC_id && <CopyableText text={SFDC_id} />) ?? ''}</div>
                {SFDC_id && (
                  <Link
                    to={{ pathname: `${salesforceUrl}/${SFDC_id}/view` }}
                    target={'_blank'}
                    data-testid="lightning-salesforce"
                    style={{ fontWeight: 500 }}
                  >
                    {'View in salesforce'}
                  </Link>
                )}
              </SummaryGridItem>
            </SummarySection>

            <SummarySection className="summary-section-internal" columns="1fr 1fr 1fr">
              <SummaryGridItem className="section-item-internal">
                <label>Pay on Account</label>
                <div>{payOnAccount}</div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal">
                <>
                  <label>Specilizations</label>
                  <div data-testid="specilizations">{specilizations?.toString()}</div>
                </>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal">
                <>
                  <label>Verified Expertise</label>
                  <div data-testid="verified-expertise">
                    {verifiedExpertise?.length ? verifiedExpertise?.toString() : 'N/A'}
                  </div>
                </>
              </SummaryGridItem>
            </SummarySection>
          </div>
        )}
      </SummaryGridItem>

      <SummaryGridItem className="summary-grid-item-internal">
        <SetPaymentMethod
          //@ts-ignore
          transactionAccountId={transactionAccountId}
          transactionAccountNumber={transactionAccountNumber}
          invoiceGroupId={invoiceGroupId}
          isTxASummarySection={true}
          refetch={editBillingDetailsFormProps.onComplete}
        />
      </SummaryGridItem>
    </SummarySection>
  );
};

export default Summary;
